import React, {
  ElementType,
  Fragment,
  lazy,
  Suspense,
  FC,
  useState,
  useEffect,
} from 'react';

import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SvgIcon,
  Typography,
} from '@mui/material';

import { useSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { XCircle as CloseIcon } from 'react-feather';
import { useLocation } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';

import { LoadingScreen } from '@cigam/template/dist/components';
import DashboardLayout from '@cigam/template/dist/layouts/DashboardLayout';
import { cdnHash, formatCpfCnpj } from '@cigam/template/dist/utils';
import { THEMES } from '@cigam/template/dist/global/constants';
import { useSettings } from '@cigam/template/dist/hooks/useSettings';
import { useDispatch, useSelector } from 'src/store';

import { AuthRoute } from 'src/components/AuthRoute';
import { GuestGuard } from 'src/components/GuestGuard';
import { MODULE } from 'src/config';
import { INavConfig, navConfig } from 'src/config/navigation';
import { isNLConfigEnabled } from 'src/config/runtimeConfigs';

import LogService from 'src/services/debug/logService';
import logAxios from 'src/utils/axiosDebug';

import type {
  ICiaAgent,
  ICiaAuthenticationContext,
  ICiaGenerativeOption,
  ICiaMenuInfo,
  ICiaMenuOpenContext,
} from 'src/Areas/Genericos/GE/types/cia';
import { canShowGenerativeOptions } from 'src/Areas/Genericos/GE/utils/ciaElement';
import {
  executeCiaGenerativeAI,
  getCiaServicesAgents,
  getCiaServicesMenuOptions,
  invokeCiaAgent,
} from 'src/Areas/Genericos/GE/services/ciaService';

import { logout } from './Areas/Genericos/GE/slices/account';
import { getAccessToken } from './Areas/Genericos/GE/services/authService';

import {
  flushRouteTracker,
  trackRoute,
} from './global/functions/routeTrackers';

const routesConfig = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/Error404View')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/configuracao',
    component: lazy(() => import('src/views/InitialConfig')),
  },
  // ROTAS DE LOGIN
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalcliente.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalclienteservicos.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalcompras.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalfinanceiro.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalindustria.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalprojetos.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: `${MODULE.portalservicos.location}/login`,
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginCigam')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/logincontato',
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/LoginContato')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/home/esqueceusuasenha',
    component: lazy(
      () => import('src/Areas/Genericos/GE/views/auth/RecuperarSenha')
    ),
  },
  {
    exact: true,
    guard: AuthRoute,
    path: '/',
    component: lazy(() => import('src/Areas/Genericos/GE/views/Solutions')),
  },
  // ROTAS DESKTOP
  {
    exact: true,
    guard: AuthRoute,
    path: `${MODULE.desktop.location}/Gantt`,
    component: lazy(() => import('src/Areas/BPM/PJ/views/Gantt')),
  },
  {
    exact: true,
    guard: AuthRoute,
    path: `${MODULE.desktop.location}/TarefasProjeto`,
    component: lazy(() => import('src/Areas/BPM/PJ/views/Tarefas')),
  },
  {
    exact: true,
    guard: AuthRoute,
    path: `${MODULE.desktop.location}/BuscaAtividadesEquipe/:codigoAtividadeFrequente`,
    component: lazy(
      () =>
        import('src/Areas/Servicos/GS/views/AtividadesFrequentes/BuscaEquipes')
    ),
  },
  {
    exact: true,
    guard: AuthRoute,
    path: `${MODULE.desktop.location}/BuscaAtividadesEquipe/ListaTecnicos`,
    component: lazy(
      () =>
        import(
          'src/Areas/Servicos/GS/views/AtividadesFrequentes/BuscaEquipes/ListaTecnicos'
        )
    ),
  },
  {
    guard: AuthRoute,
    layout: DashboardLayout,
    path: '/',
    routes: [
      // ROTAS SEGURANÇA
      {
        exact: true,
        path: `${MODULE.editarperfil.location}/Perfil`,
        component: lazy(
          () => import('src/Areas/Genericos/GE/views/PerfilUsuario/Perfil')
        ),
      },
      {
        exact: true,
        path: `${MODULE.editarperfil.location}/AutenticacaoDuasEtapas`,
        component: lazy(
          () =>
            import(
              'src/Areas/Genericos/GE/views/auth/LoginCigam/AutenticacaoDoisFatores/Cadastro'
            )
        ),
      },
      {
        exact: true,
        path: `${MODULE.editarperfil.location}/AlterarSenha`,
        component: lazy(
          () =>
            import(
              'src/Areas/Genericos/GE/views/auth/AlterarSenha/AlterarSenha'
            )
        ),
      },
      // ROTAS CLIENTE
      {
        exact: true,
        path: `${MODULE.portalcliente.location}`,
        component: () => (
          <Redirect to={`${MODULE.portalcliente.location}/Financas`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcliente.location}/Financas`,
        component: lazy(() => import('src/Areas/Financas/GF/views/')),
      },
      {
        exact: true,
        path: `${MODULE.portalcliente.location}/Boletos`,
        component: lazy(() => import('src/Areas/Financas/GF/views/Boletos')),
      },
      {
        exact: true,
        path: `${MODULE.portalcliente.location}/Contratos`,
        component: lazy(() => import('src/Areas/Financas/GF/views/Contratos')),
      },
      // ROTAS SERVICOS
      {
        exact: true,
        path: MODULE.portalservicos.location,
        component: () => (
          <Redirect to={`${MODULE.portalservicos.location}/Kanban`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalservicos.location}/Kanban`,
        component: lazy(
          () => import('src/Areas/Servicos/GS/views/Kanban/Boards')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalservicos.location}/Kanban/:sprint`,
        component: lazy(
          () => import('src/Areas/Servicos/GS/views/Kanban/KanbanView')
        ),
      },
      // ROTAS PORTAL CLIENTE/SERVICOS
      {
        exact: true,
        path: MODULE.portalclienteservicos.location,
        component: () => (
          <Redirect
            to={`${MODULE.portalclienteservicos.location}/MinhasOrdensServico`}
          />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalclienteservicos.location}/MinhasOrdensServico`,
        component: lazy(
          () =>
            import('src/Areas/Servicos/GS/views/ClienteServicos/OrdensServico/')
        ),
      },
      // ROTAS BPM
      {
        exact: true,
        path: `${MODULE.portalprojetos.location}`,
        component: () => (
          <Redirect to={`${MODULE.portalprojetos.location}/AgendaTecnicos`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalprojetos.location}/AgendaTecnicos`,
        component: lazy(() => import('src/Areas/BPM/PJ/views/AgendaTecnicos')),
      },
      // ROTAS INDUSTRIA
      {
        exact: true,
        path: `${MODULE.portalindustria.location}`,
        component: () => (
          <Redirect to={`${MODULE.portalindustria.location}/Dashboard`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalindustria.location}/Dashboard`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PC/views/Dashboard')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalindustria.location}/LeitorProducao`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PC/views/LeitorProducao')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalindustria.location}/MovimentosProducao`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PC/views/MovimentosProducao')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalindustria.location}/SequenciamentoProducao`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PC/views/SequenciamentoProducao')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalindustria.location}/PDOrdensProducao`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PC/views/PDOrdensProducao')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalindustria.location}/Relatorios/:nomePublico/:nomeRelatorio/:grupo?`,
        component: lazy(
          () => import('src/Areas/Genericos/GE/components/Relatorios')
        ),
      },
      // ROTAS FINANCEIRO
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}`,
        component: () => (
          <Redirect to={`${MODULE.portalfinanceiro.location}/Dashboard`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}/Dashboard`,
        component: lazy(
          () => import('src/Areas/Financas/GF/views/Financeiro/Dashboard')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}/PDLancamentos`,
        component: lazy(
          () => import('src/Areas/Financas/GF/views/Financeiro/PDLancamentos')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}/Painel/:idDashboard/`,
        component: lazy(
          () => import('src/Areas/Financas/GF/components/Paineis')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}/Relatorios/:nomePublico/:nomeRelatorio/:grupo?`,
        component: lazy(
          () => import('src/Areas/Genericos/GE/components/Relatorios')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}/Lancamento/:codigoLancamento?/:modal?`,
        component: lazy(
          () => import('src/Areas/Financas/GF/components/Lancamento')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalfinanceiro.location}/WebAdd/:nomePublico/:nomeArea/:nomePrograma/:argumentos?`,
        component: lazy(
          () => import('src/Areas/Genericos/GE/components/WebAdd')
        ),
      },
      // ROTAS COMPRAS
      {
        exact: true,
        path: `${MODULE.portalcompras.location}`,
        component: () => (
          <Redirect to={`${MODULE.portalcompras.location}/Dashboard`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/Dashboard`,
        component: lazy(
          () => import('src/Areas/Suprimentos/CO/views/Dashboard')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/PDNotasEntrada`,
        component: lazy(
          () => import('src/Areas/Suprimentos/CO/views/PDNotasEntrada')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/PDOrdensCompras`,
        component: lazy(
          () => import('src/Areas/Suprimentos/CO/views/PDOrdensCompras')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/PDSolicitacoesCompras`,
        component: lazy(
          () => import('src/Areas/Suprimentos/CO/views/PDSolicitacoesCompras')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/PDPreNota`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PN/views/PDPreNota')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/PDGerenciamentoXML`,
        component: lazy(
          () => import('src/Areas/Suprimentos/PN/views/PDGerenciamentoXML')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/InteligenciaNegocios/:idDashboard/`,
        component: lazy(
          () => import('src/Areas/Suprimentos/CO/components/Paineis')
        ),
      },
      {
        exact: true,
        path: `${MODULE.portalcompras.location}/Relatorios/:nomePublico/:nomeRelatorio/:grupo?`,
        component: lazy(
          () => import('src/Areas/Genericos/GE/components/Relatorios')
        ),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    component: () => <Redirect to="/404" />,
  },
];

interface IRoutes {
  exact?: boolean;
  path?: string;
  guard?: ElementType;
  layout?: ElementType;
  component?: ElementType;
  routes?: IRoutes[];
}

interface IAplicacoes {
  Portal: string;
  NomeExibicao: string;
  Nome: string;
  Endereco: string;
  Security: boolean;
  PossuiDireito: boolean;
}

export interface ISobre {
  NomeEmpresa: string;
  CNPJCPF: string;
  BancoDados: string;
  Release: string;
  Ambiente: string;
}

interface RenderRoutesProps {
  routes: IRoutes[];
  navigation: INavConfig;
  user: {
    bio: string;
    name: string;
    avatar: string;
  };
  aplicacoes: IAplicacoes[] | undefined;
  possuiA2F: boolean;
  possuiConectividade: boolean;
  tipoLogin: 'cigam' | 'contato';
  dadosSobre?: ISobre;

  handleLogout: () => void;
  apmUrl?: string;
  urlEditarPerfil?: string;
  agents?: ICiaAgent[];
  generativeOptions?: ICiaGenerativeOption[];
  loadingCiaMenu?: boolean;
  ciaMenuInfo?: ICiaMenuInfo[];
  onOpenCiaMenu?: (context: ICiaMenuOpenContext) => void;
  onCiaAgentClick?: (agent: ICiaAgent) => void;
  onCiaGenerativeClick?: (
    option: ICiaGenerativeOption,
    previousActiveElement: Element | null
  ) => void;
}

const renderRoutes: FC<RenderRoutesProps> = ({
  routes,
  navigation,
  user,
  aplicacoes,
  handleLogout,
  apmUrl = '',
  possuiA2F,
  possuiConectividade,
  tipoLogin,
  dadosSobre,
  urlEditarPerfil,
  agents = [],
  generativeOptions = [],
  ciaMenuInfo = [],
  loadingCiaMenu = false,
  onOpenCiaMenu,
  onCiaAgentClick,
  onCiaGenerativeClick,
}) => {
  return routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout;
          const Component = route.component;

          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={props => (
                // @ts-ignore
                <Guard>
                  {Layout ? (
                    // @ts-ignore
                    <Layout
                      navConfig={navigation}
                      themes={THEMES}
                      user={user}
                      logService={LogService}
                      logAxios={logAxios}
                      logMonitorUrl={apmUrl}
                      logout={handleLogout}
                      aplicacoes={aplicacoes}
                      possuiA2F={possuiA2F}
                      possuiConectividade={possuiConectividade}
                      tipoLogin={tipoLogin}
                      dadosSobre={dadosSobre}
                      urlEditarPerfil={urlEditarPerfil}
                      agents={agents}
                      generativeOptions={generativeOptions}
                      ciaMenuInfo={ciaMenuInfo}
                      loadingCiaMenu={loadingCiaMenu}
                      onOpenCiaMenu={onOpenCiaMenu}
                      onCiaAgentClick={onCiaAgentClick}
                      onCiaGenerativeClick={onCiaGenerativeClick}
                    >
                      {route.routes
                        ? renderRoutes({
                            routes: route.routes,
                            navigation,
                            user,
                            aplicacoes,
                            handleLogout,
                            possuiA2F,
                            possuiConectividade,
                            tipoLogin,
                            dadosSobre,
                            urlEditarPerfil,
                            agents,
                            generativeOptions,
                            loadingCiaMenu,
                            ciaMenuInfo,
                            onOpenCiaMenu,
                            onCiaAgentClick,
                            onCiaGenerativeClick,
                          })
                        : // @ts-ignore
                          Component && <Component {...props} />}
                    </Layout>
                  ) : (
                    <>
                      {route.routes
                        ? renderRoutes({
                            routes: route.routes,
                            navigation,
                            user,
                            aplicacoes,
                            handleLogout,
                            possuiA2F,
                            possuiConectividade,
                            tipoLogin,
                            dadosSobre,
                            urlEditarPerfil,
                            agents,
                            generativeOptions,
                            loadingCiaMenu,
                            ciaMenuInfo,
                            onOpenCiaMenu,
                            onCiaAgentClick,
                            onCiaGenerativeClick,
                          })
                        : // @ts-ignore
                          Component && <Component {...props} />}
                    </>
                  )}
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;
};

const chooseMenu = (location: { pathname: string }): INavConfig => {
  const matchNavigation = navConfig.find(mod => {
    const { moduleName } = mod;
    return location.pathname.includes(moduleName);
  });

  if (!matchNavigation) {
    return { moduleName: '', nav: [] };
  }

  return {
    ...matchNavigation,
    nav: matchNavigation.nav.map(section => ({
      ...section,
      items: section.items.filter(item => isNLConfigEnabled(item.nomeLogico)),
    })),
  };
};

const Routes: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigation = chooseMenu(location);
  const account = useSelector(state => state.account);
  const { settings } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [, updateState] = useState<any>();

  const [ciaAgents, setCiaAgents] = useState<ICiaAgent[]>([]);
  const [ciaGenerativeOptions, setCiaGenerativeOptions] = useState<
    ICiaGenerativeOption[]
  >([]);
  const [loadingCiaMenu, setLoadingCiaMenu] = useState(false);
  const [showAgentResult, setShowAgentResult] = useState(false);
  const [agentResultTitle, setAgentResultTitle] = useState('');
  const [agentResultText, setAgentResultText] = useState('');
  const [ciaMenuInfo, setCiaMenuInfo] = useState<ICiaMenuInfo[]>([]);
  const [loadingCiaAction, setLoadingCiaAction] = useState(false);

  useEffect(() => {
    updateState({});
  }, [account.direitos]);

  useEffect(() => {
    trackRoute(location.pathname, account?.user?.LoginUsuario);
  }, [location.pathname, account]);

  const handleLogout = async () => {
    try {
      await flushRouteTracker();
    } catch (err) {
      console.error(
        '[RouteTracker] Falha ao enviar acessos antes do logout',
        err
      );
    }

    dispatch(logout());
  };

  const apmUrl = () => {
    return `${settings.apmMonitorUrl}/${cdnHash()}`;
  };

  const userName = account?.user?.NomeUsuario || '';

  const dadosPortais = account?.dadosPortais?.data.Aplicacoes;
  const possuiA2F = account?.possuiA2F || false;
  const possuiConectividade = account?.possuiConectividade || false;
  const tipoLogin = account?.tipoLogin;
  const dadosSobre = account?.dadosSobre?.data.sobre;
  const urlEditarPerfil = window.location.origin.concat(
    `${MODULE.editarperfil.location}/Perfil`
  );
  const hash = getAccessToken();

  const ciaServiceUrl = dadosPortais?.find(
    portal => portal.Portal === 'CIA_SERVICE'
  )?.Endereco;

  const handleOpenCiaMenu = async (
    context: ICiaMenuOpenContext
  ): Promise<boolean> => {
    const canShowGenerative = canShowGenerativeOptions(
      context.previousActiveElement
    );

    setLoadingCiaMenu(true);
    setCiaAgents([]);
    setCiaGenerativeOptions([]);
    setCiaMenuInfo([]);

    const ciaAuthenticationContext: ICiaAuthenticationContext = {
      cnpjCpf: dadosSobre?.cnpjCpfLicenciada ?? '',
      nomeUsuario: account?.user?.NomeUsuario ?? '',
      codigoUsuario: account?.user?.LoginUsuario ?? '',
      apiDatabase: dadosSobre?.bancoDados ?? '',
      apiUrl: settings.apiUrl,
      ciaServiceUrl: ciaServiceUrl ?? '',
    };

    try {
      const [agentsResult, generativeOptionsResponse] = await Promise.all([
        getCiaServicesAgents(ciaAuthenticationContext),
        getCiaServicesMenuOptions(ciaAuthenticationContext, enqueueSnackbar),
      ]);

      const agentsResponse = agentsResult.agents;

      const hasAgents = agentsResponse.length > 0;
      const hasGenerativeOptions =
        canShowGenerative && generativeOptionsResponse.length > 0;

      const infoItems: ICiaMenuInfo[] = [];

      if (canShowGenerative && hasGenerativeOptions) {
        setCiaGenerativeOptions(generativeOptionsResponse);

        if (!hasAgents) {
          infoItems.push({
            text: '🚫 Nenhum agente disponível para o que você está fazendo neste momento',
          });
        }
      } else {
        setCiaGenerativeOptions([]);
      }

      if (hasAgents) {
        setCiaAgents(agentsResponse);

        if (!canShowGenerative) {
          infoItems.push({
            text: '🚫 Geração de conteúdo não disponível aqui',
            tooltip:
              'Para utilizar a Geração de conteúdo posicione-se em um campo de texto que não esteja vazio e contenha mais de três palavras.',
          });
        }
      } else {
        setCiaAgents([]);
      }

      setCiaMenuInfo(infoItems);

      if (hasAgents || hasGenerativeOptions) {
        return true;
      }

      if (!canShowGenerative) {
        enqueueSnackbar(
          'Para utilizar a Geração de conteúdo posicione-se em um campo de texto que não esteja vazio e contenha mais de três palavras.',
          {
            variant: 'warning',
          }
        );
      }

      if (!hasAgents) {
        enqueueSnackbar(
          agentsResult.message ||
            'Os agentes não estão disponíveis nesse momento.',
          {
            variant: 'error',
          }
        );
      }

      return false;
    } catch (error) {
      console.error(
        'Ocorreu um erro interno para carregar o menu da Cia:',
        error
      );

      enqueueSnackbar(
        'Ocorreu um erro ao tentar carregar as opções de menu da Cia.',
        {
          variant: 'error',
        }
      );

      setCiaAgents([]);
      setCiaGenerativeOptions([]);
      setCiaMenuInfo([]);

      return false;
    } finally {
      setLoadingCiaMenu(false);
    }
  };

  const handleCiaAgentClick = async (agent: ICiaAgent) => {
    const ciaAuthenticationContext: ICiaAuthenticationContext = {
      cnpjCpf: dadosSobre?.cnpjCpfLicenciada ?? '',
      nomeUsuario: account?.user?.NomeUsuario ?? '',
      codigoUsuario: account?.user?.LoginUsuario ?? '',
      apiDatabase: dadosSobre?.bancoDados ?? '',
      apiUrl: settings.apiUrl,
      ciaServiceUrl: ciaServiceUrl ?? '',
    };

    await invokeCiaAgent(
      agent,
      ciaAuthenticationContext,
      (title, content) => {
        setAgentResultTitle(title);
        setAgentResultText(content);
        setShowAgentResult(true);
      },
      enqueueSnackbar,
      setLoadingCiaAction
    );
  };

  const handleCiaGenerativeClick = async (
    option: ICiaGenerativeOption,
    previousActiveElement: Element | null
  ) => {
    const ciaAuthenticationContext: ICiaAuthenticationContext = {
      cnpjCpf: dadosSobre?.cnpjCpfLicenciada ?? '',
      nomeUsuario: account?.user?.NomeUsuario ?? '',
      codigoUsuario: account?.user?.LoginUsuario ?? '',
      apiDatabase: dadosSobre?.bancoDados ?? '',
      apiUrl: settings.apiUrl,
      ciaServiceUrl: ciaServiceUrl ?? '',
    };

    await executeCiaGenerativeAI(
      option,
      previousActiveElement,
      ciaAuthenticationContext,
      enqueueSnackbar,
      setLoadingCiaAction
    );
  };

  const aplicacoes: IAplicacoes[] | undefined = dadosPortais?.map(data => {
    const hashEndereco = data.Endereco
      ? data.Endereco.concat(hash ? '?token='.concat(hash) : '')
      : '';

    const aplicacao: IAplicacoes = {
      Portal: data.Portal,
      NomeExibicao: data.NomeExibicao,
      Nome: data.Nome,
      Endereco:
        data.Portal === 'CIA'
          ? data.Endereco.concat('?cigam=')
              .concat(settings.apiUrl)
              .concat(hash ? '&token='.concat(hash) : '')
          : hashEndereco,
      PossuiDireito: data.PossuiDireito,
      Security: data.Security,
    };

    return aplicacao;
  });

  const user = {
    bio: userName,
    name: userName,
    avatar: '',
  };

  return (
    <>
      {renderRoutes({
        routes: routesConfig,
        navigation,
        user,
        aplicacoes,
        handleLogout,
        apmUrl: apmUrl(),
        possuiA2F,
        possuiConectividade,
        tipoLogin,
        dadosSobre: {
          NomeEmpresa:
            dadosSobre?.fantasiaLicenciada
              .concat(' | ')
              .concat(dadosSobre?.razaoSocialLicenciada) ?? ' ',
          CNPJCPF: formatCpfCnpj(dadosSobre?.cnpjCpfLicenciada ?? ' '),
          BancoDados:
            dadosSobre?.bancoDados
              .concat(' ')
              .concat(dadosSobre?.descricaoBancoDados) ?? ' ',
          Release: dadosSobre?.fullRelease ?? ' ',
          Ambiente: dadosSobre?.descricaoAmbiente ?? ' ',
        },
        urlEditarPerfil,
        agents: ciaAgents,
        generativeOptions: ciaGenerativeOptions,
        loadingCiaMenu,
        ciaMenuInfo,
        onOpenCiaMenu: handleOpenCiaMenu,
        onCiaAgentClick: handleCiaAgentClick,
        onCiaGenerativeClick: handleCiaGenerativeClick,
      })}

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 999 }}
        open={loadingCiaMenu || loadingCiaAction}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {showAgentResult && (
        <Dialog
          open={showAgentResult}
          onClose={() => setShowAgentResult(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: '1.15rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {agentResultTitle || 'Resultado do Agente'}
            <IconButton onClick={() => setShowAgentResult(false)}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {parse(agentResultText)}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowAgentResult(false)}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Routes;
